import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormSyncToHash } from '@/hooks/useFormSyncToHash';
import { useBusinessParameters } from '@/hooks/useParameters';

const formSchema = z.object({
  businessType: z.enum(['free', 'paid']),
  discount_amount: z.number().min(0, {
    message: "Discount amount cannot be negative.",
  }).max(999999, {
    message: "Discount amount is too large.",
  }),
  is_percentage: z.boolean(),
  applies_to_all: z.boolean().optional(),
  discount_description: z.string().optional(),
}).refine((data) => {
  // For paid businesses, discount amount must be greater than 0
  if (data.businessType === 'paid' && data.discount_amount <= 0) {
    return false;
  }
  return true;
}, {
  message: "Paid businesses must provide a discount greater than 0.",
  path: ["discount_amount"]
});

type FormValues = z.infer<typeof formSchema>;

interface BusinessRegistrationDiscountFormProps {
  formData: {
    businessType?: 'free' | 'paid';
    discount_amount?: number;
    is_percentage?: boolean;
    applies_to_all?: boolean;
    discount_description?: string;
  };
  updateFormData: (data: Partial<{
    businessType: 'free' | 'paid';
    discount_amount: number;
    is_percentage: boolean;
    applies_to_all: boolean;
    discount_description: string;
  }>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const BusinessRegistrationDiscountForm = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}: BusinessRegistrationDiscountFormProps) => {
  const { partialDiscountAllowed, isLoading: parametersLoading } = useBusinessParameters();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      businessType: formData.businessType || 'paid',
      discount_amount: formData.discount_amount || 10,
      is_percentage: formData.is_percentage !== undefined ? formData.is_percentage : true,
      applies_to_all: formData.applies_to_all !== undefined ? formData.applies_to_all : true,
      discount_description: formData.discount_description || '',
    },
  });

  // Use the custom hook to sync form data to URL hash
  useFormSyncToHash(form.watch, formData, updateFormData);

  const businessType = form.watch('businessType');
  const isPercentage = form.watch('is_percentage');
  const maxValue = isPercentage ? 100 : 999999;
  const minValue = businessType === 'free' ? 0 : (isPercentage ? 5 : 0.01);

  const onSubmit = (values: FormValues) => {
    // For free businesses, set discount amount to 0 and override other discount fields
    const finalValues = values.businessType === 'free' ? {
      ...values,
      discount_amount: 0,
      is_percentage: true,
      applies_to_all: true,
      discount_description: ''
    } : {
      ...values,
      applies_to_all: partialDiscountAllowed ? values.applies_to_all : true
    };
    
    updateFormData(finalValues);
    onNext();
  };

  const handlePercentageToggle = (checked: boolean) => {
    form.setValue('is_percentage', checked);
    if (businessType === 'paid') {
      form.setValue('discount_amount', checked ? 10 : 5);
    }
  };

  const handleBusinessTypeChange = (type: 'free' | 'paid') => {
    form.setValue('businessType', type);
    if (type === 'free') {
      form.setValue('discount_amount', 0);
    } else {
      form.setValue('discount_amount', isPercentage ? 10 : 5);
    }
  };

  // Show loading state while parameters are being fetched
  if (parametersLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Business Registration Type</h2>
        <p className="text-muted-foreground">
          Choose how you'd like to register your business. Free businesses are automatically listed when approved by staff.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">

          {/* Description field - show for all business types */}
          <FormField
            control={form.control}
            name="businessType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Business Type</FormLabel>
                <FormControl>
                  <div className="flex items-center space-x-3 p-4 border rounded-lg h-12">
                    <Switch
                      checked={field.value === 'paid'}
                      onCheckedChange={(checked) => field.onChange(checked ? 'paid' : 'free')}
                    />
                    <Label className="cursor-pointer text-base">
                      {field.value === 'paid' ? 'Paid Business (with community discount)' : 'Free Business (no discount required)'}
                    </Label>
                  </div>
                </FormControl>
                <FormDescription className="text-sm mt-2">
                  {field.value === 'paid' 
                    ? 'Offer a discount to community members to attract customers and stand out.'
                    : 'List your business for free. Auto-approved and listed when approved by staff.'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Discount Fields - Only show for paid businesses */}
          {businessType === 'paid' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="discount_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">
                        Discount Amount {isPercentage ? '(%)' : '(£)'}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step={isPercentage ? "1" : "0.01"}
                          min={minValue}
                          max={maxValue}
                          value={field.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              field.onChange('');
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          className="h-12 text-base"
                        />
                      </FormControl>
                      <FormDescription className="text-sm mt-2">
                        The {isPercentage ? 'percentage' : 'fixed amount'} discount you'll offer to community members.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_percentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Discount Type</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-3 p-4 border rounded-lg h-12">
                          <Switch
                            checked={field.value}
                            onCheckedChange={handlePercentageToggle}
                          />
                          <Label className="cursor-pointer text-base">
                            {field.value ? 'Percentage (%)' : 'Fixed Amount (£)'}
                          </Label>
                        </div>
                      </FormControl>
                      <FormDescription className="text-sm mt-2">
                        Choose whether to offer a percentage discount or a fixed amount off.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {partialDiscountAllowed && (
                <FormField
                  control={form.control}
                  name="applies_to_all"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Discount Coverage</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-3 p-4 border rounded-lg">
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <Label className="cursor-pointer text-base">
                            {field.value ? 'Apply to all services' : 'Apply to selected services only'}
                          </Label>
                        </div>
                      </FormControl>
                      <FormDescription className="text-sm mt-2">
                        {field.value 
                          ? 'This discount will apply to all your services automatically.' 
                          : 'You can choose which specific services this discount applies to later.'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="discount_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base">
                      Description <span className="text-muted-foreground">(Optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe any special terms or conditions for this discount..."
                        className="min-h-[120px] text-base"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-sm mt-2">
                      Add any special terms, conditions, or details about your discount offer.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          <div className="flex justify-between pt-8">
            <Button type="button" variant="outline" onClick={onPrev} size="lg" className="px-8">
              Previous
            </Button>
            <Button type="submit" size="lg" className="px-8">
              Next
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BusinessRegistrationDiscountForm;